import { BillingPanel } from '@/components/BillingPanel';
import { WorkspaceAccountSettings } from '@/components/WorkspaceAccountSettings';
import { IncomingWebhooksPanel } from '@/features/incoming-webhooks';
import { listSyncedItems } from '@/features/integrations/api';
import { WorkflowSettingsPanel } from '@/features/workflows';
import { WebPushSettings } from '@/features/notifications';
import { SharedChannelsPanel } from '@/features/shared-channels/SharedChannelsPanel';
import { SsoLinkPanel } from '@/features/sso';
import { DesktopUpdateStatus } from '@/components/DesktopUpdateStatus';
import { isTauri } from '@tauri-apps/api/core';
/**
 * SettingsPage — multi-tab settings shell.
 *
 * Sections (matching docs/chatwork_killer/09_security_sso_audit.md and
 * docs/chatwork_killer/06_task_file_org.md):
 *
 *   /settings/profile        — Display name / email / avatar / language / tone
 *   /settings/workspace      — Workspace name / domain / branding
 *   /settings/members        — Member roster + invite + roles
 *   /settings/integrations   — freee / マネーフォワード / kintone / Google / Microsoft 365 / Salesforce
 *   /settings/billing        — Plan, seats, invoice email, upgrade
 *   /settings/security       — MFA / SSO / SCIM / session policy
 *   /settings/dlp            — Mask patterns + actions per pattern
 *   /settings/reports        — UGC report queue (tenant owner/admin)
 *   /settings/content-filter — Literal content-filter terms (tenant owner/admin)
 *   /settings/audit          — Tamper-proof audit log viewer
 *   /settings/compliance     — ISMAP / SOC2 / region pinning
 *
 * All sections render in a right pane next to a vertical Tabs nav.
 * Light theme only.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  NavLink,
  useNavigate,
  useLocation,
  useSearchParams,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  CreditCard,
  FileLock,
  Filter,
  Flag,
  KeyRound,
  Plug,
  Scale,
  ScrollText,
  ShieldCheck,
  User,
  UserCog,
} from "lucide-react";

import { MigrationPage } from "@/pages/MigrationPage";
import { TopBar } from "@/components/TopBar";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, Textarea } from "@/components/ui/Input";
import { useSession, setSession, getSession, sessionScope } from "@/lib/auth";
import { useMembers, useWorkspace } from "@/lib/workspace";
import { api, apiGet, apiPost, apiPatch, apiDelete, authenticatedFetch, resolveApiUrl } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import {PersonalSecurity} from '@/features/auth/PersonalSecurity';
import {RetentionSettings} from '@/features/auth/RetentionSettings';
import {AccountDeletionPanel} from '@/features/auth/AccountDeletionPanel';
import { ContentFilterAdmin, ReportsAdmin, useCanManageUgc } from "@/features/admin/ugc";
import {
  getFontScale,
  getTone,
  setFontScale,
  setTone,
  type FontScale,
  type Tone,
} from "@/lib/i18n";

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <TopBar
        title={t("settings.title")}
        subtitle={t("profilePage.settingsDescription")}
      />
      <div className="flex flex-1 min-h-0 bg-surface-base">
        <SettingsNav />
        <main
          className="flex-1 overflow-auto"
          aria-live="polite"
          role="region"
          tabIndex={0}
          aria-label={t("settings.title")}
        >
          <div className="mx-auto max-w-3xl p-4 md:p-8">
            <MobileSettingsNav />
            <Routes>
              <Route index element={<Navigate to="/settings/profile" replace />} />
              <Route path="profile" element={<ProfileTab />} />
              <Route path="workspace" element={<WorkspaceTab />} />
              <Route path="migration" element={<MigrationPage />} />
              <Route path="members" element={<MembersTab />} />
              <Route path="integrations" element={<IntegrationsTab />} />
              <Route path="workflows" element={<WorkflowSettingsPanel />} />
              <Route path="shared-channels" element={<SharedChannelsPanel />} />
              <Route path="billing" element={<BillingTab />} />
              <Route path="security" element={<SecurityTab />} />
              <Route path="dlp" element={<DlpTab />} />
              <Route path="reports" element={<ReportsAdmin />} />
              <Route path="content-filter" element={<ContentFilterAdmin />} />
              <Route path="audit" element={<AuditTab />} />
              <Route path="compliance" element={<ComplianceTab />} />
              <Route path="*" element={<Navigate to="/settings/profile" replace />} />
            </Routes>
          </div>
        </main>
      </div>
      <Outlet />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Toast feedback (lightweight, page-local)                                    */
/* -------------------------------------------------------------------------- */

type ToastTone = "success" | "warning" | "danger";

interface ToastState {
  id: number;
  tone: ToastTone;
  message: string;
}

let toastSeq = 0;
const TOAST_EVENT = "ailechat:settings-toast";

function emitToast(tone: ToastTone, message: string): void {
  if (typeof window === "undefined") return;
  toastSeq += 1;
  const detail: ToastState = { id: toastSeq, tone, message };
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail }));
}

function useToasts(): ToastState[] {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  useEffect(() => {
    function onToast(e: Event): void {
      const t = (e as CustomEvent<ToastState>).detail;
      if (!t) return;
      setToasts((prev) => [...prev, t]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
      }, 2400);
    }
    window.addEventListener(TOAST_EVENT, onToast);
    return () => window.removeEventListener(TOAST_EVENT, onToast);
  }, []);
  return toasts;
}

function ToastStack(): JSX.Element {
  const toasts = useToasts();
  if (toasts.length === 0) return <></>;
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-6 right-6 z-50 flex flex-col gap-2"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            "pointer-events-auto flex items-center gap-2 rounded-md px-4 py-2 shadow-md ring-1",
            t.tone === "success" &&
              "bg-success-bg text-success ring-success/30",
            t.tone === "warning" &&
              "bg-warning-bg text-warning ring-warning/30",
            t.tone === "danger" && "bg-danger-bg text-danger ring-danger/30",
          )}
        >
          {t.tone === "success" ? (
            <CheckCircle2 size={14} aria-hidden />
          ) : (
            <AlertTriangle size={14} aria-hidden />
          )}
          <span className="text-sm">{t.message}</span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Navigation                                                                 */
/* -------------------------------------------------------------------------- */

interface NavItem {
  to: string;
  label: string;
  icon: typeof User;
  group?: "personal" | "workspace" | "security";
  adminOnly?: boolean;
}

function buildNav(t: (k: string) => string): NavItem[] {
  return [
    { to: "profile", label: t("settings.profile"), icon: User, group: "personal" },
    { to: "workspace", label: t("settings.workspace"), icon: Building2, group: "workspace" },
    { to: "members", label: t("common.members"), icon: UserCog, group: "workspace" },
    { to: "migration", label: t("profilePage.navigation.migration"), icon: Building2, group: "workspace" },
    { to: "integrations", label: t("profilePage.navigation.integrations"), icon: Plug, group: "workspace" },
    { to: "workflows", label: t("profilePage.navigation.workflows"), icon: Plug, group: "workspace" },
    { to: "shared-channels", label: t("profilePage.navigation.sharedChannels"), icon: Building2, group: "workspace" },
    { to: "billing", label: t("settings.billing"), icon: CreditCard, group: "workspace" },
    { to: "security", label: t("profilePage.navigation.security"), icon: ShieldCheck, group: "security" },
    { to: "dlp", label: t("profilePage.navigation.dlp"), icon: FileLock, group: "security" },
    { to: "reports", label: t("profilePage.navigation.reports"), icon: Flag, group: "security", adminOnly: true },
    { to: "content-filter", label: t("profilePage.navigation.contentFilter"), icon: Filter, group: "security", adminOnly: true },
    { to: "audit", label: t("profilePage.navigation.audit"), icon: ScrollText, group: "security" },
    { to: "compliance", label: t("profilePage.navigation.compliance"), icon: Scale, group: "security" },
  ];
}

function useVisibleNav(t: (k: string) => string): NavItem[] {
  const canManageUgc = useCanManageUgc();
  return useMemo(
    () => buildNav(t).filter((item) => !item.adminOnly || canManageUgc),
    [t, canManageUgc],
  );
}

function MobileSettingsNav() {
  const { t } = useTranslation(); const navigate = useNavigate(); const location = useLocation();
  const items = useVisibleNav(t);
  return <select aria-label={t("profilePage.navigation.category")} value={location.pathname.split("/")[2] || "profile"} onChange={e => navigate(`/settings/${e.target.value}`)} className="md:hidden w-full border rounded p-3 mb-5">{items.map(item => <option key={item.to} value={item.to}>{item.label}</option>)}</select>;
}

function SettingsNav(): JSX.Element {
  const { t } = useTranslation();
  const items = useVisibleNav(t);
  const groups = [
    { id: "personal", title: t("profilePage.navigation.account") },
    { id: "workspace", title: t("settings.workspace") },
    { id: "security", title: t("profilePage.navigation.governance") },
  ] as const;

  return (
    <nav
      aria-label={t("settings.title")}
      className="hidden w-60 shrink-0 flex-col border-r border-border-subtle bg-canvas md:flex"
    >
      <div className="flex flex-col gap-4 p-4">
        {groups.map((g) => (
          <div key={g.id}>
            <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
              {g.title}
            </p>
            <ul className="space-y-0.5">
              {items
                .filter((i) => i.group === g.id)
                .map((it) => (
                  <li key={it.to}>
                    <NavLink
                      to={`/settings/${it.to}`}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                          isActive
                            ? "bg-brand-50 font-medium text-brand-700"
                            : "text-fg-secondary hover:bg-surface-raised",
                        )
                      }
                    >
                      <it.icon size={14} aria-hidden />
                      <span>{it.label}</span>
                    </NavLink>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable section primitives                                                */
/* -------------------------------------------------------------------------- */

function SectionHeader({
  title,
  description,
}: {
  title: string;
  description?: string;
}): JSX.Element {
  return (
    <header className="mb-6 border-b border-border-subtle pb-4">
      <h1 className="text-xl font-semibold text-fg-primary">{title}</h1>
      {description && (
        <p className="mt-1 text-sm text-fg-secondary">{description}</p>
      )}
    </header>
  );
}

function Card({
  title,
  description,
  children,
  footer,
}: {
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  return (
    <section className="mb-4 overflow-hidden rounded-lg bg-canvas shadow-sm ring-1 ring-border-subtle">
      {(title || description) && (
        <header className="border-b border-border-subtle px-5 py-3">
          {title && (
            <h2 className="text-sm font-semibold text-fg-primary">{title}</h2>
          )}
          {description && (
            <p className="mt-0.5 text-xs text-fg-muted">{description}</p>
          )}
        </header>
      )}
      <div className="p-5">{children}</div>
      {footer && (
        <div className="flex items-center justify-end gap-2 border-t border-border-subtle bg-surface-base px-5 py-3">
          {footer}
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  helperText,
  error,
  children,
}: {
  label: string;
  helperText?: string;
  error?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-fg-secondary">{label}</span>
      {children}
      {error ? (
        <span className="text-xs text-danger">{error}</span>
      ) : helperText ? (
        <span className="text-xs text-fg-muted">{helperText}</span>
      ) : null}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:shadow-focus",
        checked ? "bg-brand-500" : "bg-fg-subtle",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-canvas shadow transition-transform",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-base px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-fg-primary">{label}</p>
        {description && (
          <p className="text-xs text-fg-muted">{description}</p>
        )}
      </div>
      <Toggle checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Profile                                                                    */
/* -------------------------------------------------------------------------- */

interface ProfileRecord { display_name: string; bio?: string; email: string; avatar_url?: string | null; preferences?: { notifications?: boolean; email_digest?: boolean }; }
function ProfileTab(): JSX.Element {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const profile = useQuery({ queryKey: ["profile", session?.user.id], refetchOnWindowFocus: false, queryFn: () => apiGet<{ user: ProfileRecord }>("/v1/me/profile") });
  useEffect(() => { if (profile.data) { setName(profile.data.user.display_name); setBio(profile.data.user.bio || ""); setAvatarUrl(profile.data.user.avatar_url || ""); setNotifications(profile.data.user.preferences?.notifications ?? true); setDigest(profile.data.user.preferences?.email_digest ?? false); } }, [profile.data]);
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [showAvatarInput, setShowAvatarInput] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [digest, setDigest] = useState(false);
  const [tone, setToneState] = useState<Tone>(() => getTone());
  const [scale, setScaleState] = useState<FontScale>(() => getFontScale());

  useEffect(() => {
    document.documentElement.setAttribute("data-font-scale", scale);
  }, [scale]);

  async function save(): Promise<void> {
    setSaving(true);
    try {
      const result = await apiPatch<{ user: ProfileRecord }>("/v1/me/profile", { display_name: name, bio, avatar_url: avatarUrl || null, preferences: { notifications, email_digest: digest } });
      if (session && getSession()?.user.id === session.user.id) { const current = getSession()!; setSession({ ...current, user: { ...current.user, displayName: result.user.display_name, avatarUrl: result.user.avatar_url } }); }
      await profile.refetch();
      emitToast("success", t("profilePage.saved"));
    } catch (e) { emitToast("danger", e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  }

  return (
    <>
      <SectionHeader
        title={t("settings.profile")}
        description={t("profilePage.description")}
      />
      <ToastStack />

      {profile.error && <p role="alert" className="text-danger">{profile.error.message}</p>}
      <Card title={t("profilePage.basicInfo")}>
        <div className="flex items-center gap-4">
          <Avatar name={name} imageUrl={avatarUrl || session?.user.avatarUrl} size={48} />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{name}</p>
            <p className="text-xs text-fg-muted">{(session?.user.email || "")}</p>
            <Button size="sm" variant="secondary" className="mt-1 w-max" onClick={() => setShowAvatarInput(v => !v)}>
              {t("profilePage.changeAvatar")}
            </Button>
            {showAvatarInput && <Field label={t("profilePage.avatarUrl")} helperText={t("profilePage.avatarUrlHelp")}><Input type="url" value={avatarUrl} onChange={e => setAvatarUrl(e.target.value)} /></Field>}
          </div>
        </div>
      </Card>

      <Card
        title={t("profilePage.identity")}
        description={t("profilePage.identityHelp")}
        footer={<Button onClick={() => void save()} disabled={saving || !profile.data || name.trim().length < 2}>{t("common.save")}</Button>}
      >
        <div className="space-y-4">
          <Field label={t("profilePage.displayName")} helperText={t("profilePage.displayNameHelp")}>
            <Input
              value={name}
              disabled={profile.isPending || saving}
              onChange={(e) => setName(e.target.value)}
              maxLength={32}
              invalid={name.length < 2}
            />
          </Field>
          <Field label={t("profilePage.bio")} helperText={t("profilePage.bioHelp")}>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              maxLength={140}
            />
          </Field>
          <Field label={t("profilePage.email")} helperText={t("profilePage.emailHelp")}>
            <Input value={(session?.user.email || "")} readOnly />
          </Field>
        </div>
      </Card>

      <Card
        title={t("settings.appearance")}
        description={t("profilePage.appearanceHelp")}
      >
        <div className="space-y-4">
          <Field label={t("settings.language")}>
            <div className="flex gap-2">
              <Button
                variant={
                  i18n.language?.startsWith("ja") ? "primary" : "secondary"
                }
                size="sm"
                onClick={() => void i18n.changeLanguage("ja-JP")}
              >
                日本語
              </Button>
              <Button
                variant={
                  i18n.language?.startsWith("en") ? "primary" : "secondary"
                }
                size="sm"
                onClick={() => void i18n.changeLanguage("en-US")}
              >
                English
              </Button>
            </div>
          </Field>

          <Field
            label={t("settings.tone")}
            helperText={t("profilePage.toneHelp")}
          >
            <div className="flex flex-wrap gap-2">
              {(["polite", "casual", "honorific"] as const).map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={tone === option ? "primary" : "secondary"}
                  onClick={() => {
                    setToneState(option);
                    setTone(option);
                  }}
                >
                  {t(`settings.tones.${option}`)}
                </Button>
              ))}
            </div>
          </Field>

          <Field
            label={t("settings.fontScale")}
            helperText={t("profilePage.fontScaleHelp")}
          >
            <div className="flex flex-wrap gap-2">
              {(["100", "125", "150"] as const).map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={scale === option ? "primary" : "secondary"}
                  onClick={() => {
                    setScaleState(option);
                    setFontScale(option);
                  }}
                >
                  {t(`settings.fontScales.${option}`)}
                </Button>
              ))}
            </div>
          </Field>
        </div>
      </Card>

      <Card title={t("settings.notifications")} footer={<Button onClick={() => void save()} disabled={saving || !profile.data}>{t("profilePage.saveNotifications")}</Button>}>
        <div className="space-y-2">
          <WebPushSettings />
          <ToggleRow
            label={t("profilePage.mentionNotifications")}
            description={t("profilePage.mentionNotificationsHelp")}
            checked={notifications}
            onChange={setNotifications}
          />
          <ToggleRow
            label={t("profilePage.emailDigest")}
            description={t("profilePage.emailDigestHelp")}
            checked={digest}
            onChange={setDigest}
          />
        </div>
      </Card>
      {isTauri() && <Card title={t("profilePage.aboutApp")}><DesktopUpdateStatus /></Card>}
      <AccountDeletionPanel/>
      {session && <SsoLinkPanel key={sessionScope(session)} token={session.token} />}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Workspace                                                                  */
/* -------------------------------------------------------------------------- */

function WorkspaceTab(): JSX.Element {
  const [params] = useSearchParams();
  const workspace = useWorkspace(params.get("workspace"));
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (workspace.data) { setName(workspace.data.name); setDomain(workspace.data.slug || ""); setDefaultLang(workspace.data.default_language || "ja-JP"); setTimezone(workspace.data.timezone || "Asia/Tokyo"); setBranding(workspace.data.branding || { show_logo: true, show_company_footer: false }); } }, [workspace.data]);
  const save = async () => {
    if (!workspace.data) return; setSaving(true);
    try { await apiPatch(`/v1/workspaces/${encodeURIComponent(workspace.data.id)}`, { name, slug: domain, default_language: defaultLang, timezone, branding }); await workspace.refetch(); emitToast("success", "ワークスペースを保存しました"); }
    catch (e) { emitToast("danger", e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };
  const [domain, setDomain] = useState("");
  const [defaultLang, setDefaultLang] = useState("ja-JP");
  const [timezone, setTimezone] = useState("Asia/Tokyo");
  const [branding, setBranding] = useState({ show_logo: true, show_company_footer: false });

  return (
    <>
      <SectionHeader
        title="ワークスペース"
        description="組織情報とブランディング設定。"
      />
      <ToastStack />

      {workspace.error && <p role="alert" className="text-danger">{workspace.error.message}</p>}
      {workspace.isSuccess && !workspace.data && <p role="alert" className="text-danger">このワークスペースを管理する権限がありません。</p>}
      {workspace.data && <div className="mb-5"><WorkspaceAccountSettings key={workspace.data.id} workspace={workspace.data} /></div>}
      {workspace.data && <div className="mb-5"><IncomingWebhooksPanel key={`hooks:${workspace.data.id}`} workspace={workspace.data} /></div>}
      <Card
        title="組織情報"
        footer={
          <Button
            onClick={() => void save()}
            disabled={saving || !workspace.data || !name.trim()}
          >
            保存
          </Button>
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="ワークスペース名">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={64}
            />
          </Field>
          <Field
            label="ドメイン"
            helperText="ワークスペースの識別名（英小文字・数字・ハイフン）。"
          >
            <Input
              value={domain}
              onChange={(e) => setDomain(e.target.value.toLowerCase())}
              pattern="[a-z0-9-]+"
            />
          </Field>
          <Field label="デフォルト言語">
            <select
              value={defaultLang}
              onChange={(e) => setDefaultLang(e.target.value)}
              className="h-10 w-full rounded-md border border-border-default bg-canvas px-3 text-sm focus-visible:shadow-focus"
            >
              <option value="ja-JP">日本語 (ja-JP)</option>
              <option value="en-US">English (en-US)</option>
            </select>
          </Field>
          <Field
            label="タイムゾーン"
            helperText="通知・期限の判定に使用します。"
          >
            <select value={timezone} onChange={e => setTimezone(e.target.value)} className="h-10 w-full rounded-md border border-border-default bg-canvas px-3 text-sm focus-visible:shadow-focus">
              <option value="Asia/Tokyo">Asia/Tokyo (JST, UTC+9)</option>
              <option>UTC</option>
              <option>America/Los_Angeles</option>
            </select>
          </Field>
        </div>
      </Card>

      <Card title="ブランディング" footer={<Button onClick={() => void save()} disabled={saving || !workspace.data}>表示設定を保存</Button>}>
        <div className="space-y-3">
          <ToggleRow
            label="ロゴをサイドバーに表示"
            checked={branding.show_logo}
            onChange={show_logo => setBranding({ ...branding, show_logo })}
          />
          <ToggleRow
            label="フッターに会社名を表示"
            checked={branding.show_company_footer}
            onChange={show_company_footer => setBranding({ ...branding, show_company_footer })}
          />
        </div>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Members                                                                    */
/* -------------------------------------------------------------------------- */

function MembersTab(): JSX.Element {
  const [filter, setFilter] = useState("");
  const { members, workspace, error, refetch } = useMembers();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [invitedEmail, setInvitedEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const invite = async () => {
    if (!workspace || !inviteEmail.trim()) return; setSaving(true);
    try { const result = await apiPost<{ accept_url: string; email_sent?: boolean; email?: string | null }>(`/v1/auth/tenants/${encodeURIComponent(workspace.id)}/invite`, { email: inviteEmail.trim(), role: "member" }); setInviteUrl(result.accept_url); setInvitedEmail(result.email_sent ? result.email ?? inviteEmail.trim() : ""); emitToast("success", result.email_sent ? "招待メールを送りました" : "招待リンクを発行しました"); }
    catch (e) { emitToast("danger", e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };
  const updateRole = async (id: string, role: string) => {
    if (!workspace) return;
    try { await apiPatch(`/v1/workspaces/${encodeURIComponent(workspace.id)}/members/${encodeURIComponent(id)}`, { role }); await refetch(); emitToast("success", "ロールを更新しました"); }
    catch (e) { emitToast("danger", e instanceof Error ? e.message : String(e)); }
  };
  const visible = members.filter((u) =>
    (u.displayName + u.email).toLowerCase().includes(filter.toLowerCase()),
  );
  return (
    <>
      <SectionHeader
        title="メンバー"
        description="ワークスペースに参加しているメンバーとロールを管理します。"
      />
      <ToastStack />

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="search"
            placeholder="名前またはメールで検索..."
            aria-label="メンバーを検索"
            className="!h-9 max-w-xs"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Badge tone="brand">{members.length} 名</Badge>
          <Input aria-label="招待先のメールアドレス" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="招待先のメールアドレス" />
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => void invite()}
            disabled={saving || !inviteEmail.trim() || !workspace}
          >
            メンバーを招待
          </Button>
        </div>

        {error && <p role="alert" className="text-danger">{error.message}</p>}
        {inviteUrl && <div role="status" className="mt-3">{invitedEmail ? <><p>{invitedEmail} に招待メールを送りました。</p><p className="text-sm text-fg-secondary">メールが見つからないときは、このリンクを直接お送りください（7 日間有効）。</p></> : <p>招待する方にこのリンクを共有してください（7 日間有効）。</p>}<Input aria-label="招待リンク" value={inviteUrl} readOnly /></div>}
        <div className="mt-4 overflow-x-auto rounded-md ring-1 ring-border-subtle">
          <table className="w-full text-sm">
            <thead className="bg-surface-base text-left text-xs text-fg-muted">
              <tr>
                <th className="px-4 py-2 font-medium">名前</th>
                <th className="px-4 py-2 font-medium">メール</th>
                <th className="px-4 py-2 font-medium">ロール</th>
                <th className="px-4 py-2 font-medium">状態</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle bg-canvas">
              {visible.map((u, i) => (
                <tr key={u.id} className="hover:bg-surface-base">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Avatar name={u.displayName} size={24} />
                      <span className="font-medium text-fg-primary">
                        {u.displayName}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-fg-secondary">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <select
                      value={u.role}
                      disabled={u.role === "owner"}
                      onChange={e => void updateRole(u.id, e.target.value)}
                      className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs focus-visible:shadow-focus"
                    >
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                      <option value="guest">Guest</option>
                    </select>
                  </td>
                  <td className="px-4 py-2.5">
                    {u.presence === "online" ? (
                      <Badge tone="success">オンライン</Badge>
                    ) : u.presence === "away" ? (
                      <Badge tone="warning">離席中</Badge>
                    ) : (
                      <Badge tone="neutral">状態不明</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="text-fg-muted">{u.id}</span>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-sm text-fg-muted"
                  >
                    一致するメンバーがいません。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Integrations                                                               */
/* -------------------------------------------------------------------------- */

type IntegrationProvider = "gmail" | "outlook" | "freee" | "moneyforward" | "kintone" | "salesforce";
interface IntegrationConnection { id: string; provider: IntegrationProvider; status: string; account_label: string | null; installed_at: number; last_synced_at: number | null; last_error: string | null; can_read_items?: boolean; client_source?: "central" | "tenant"; subdomain?: string | null; apps?: { app_id: string; name: string | null }[]; login_url?: string; }
interface IntegrationsResponse {
  integrations: IntegrationConnection[];
  available: Partial<Record<IntegrationProvider, boolean>>;
  client_setup: IntegrationProvider[];
  clients: Partial<Record<IntegrationProvider, { client_id: string; subdomain: string | null; updated_at: number }>>;
  scopes: Partial<Record<IntegrationProvider, string[]>>;
  can_manage: boolean;
  redirect_uris: Partial<Record<IntegrationProvider, string>>;
}

// 説明文は api/src/integrations/runtime.ts の各社の取り込み処理がやることと同じにする（変えるときは両方を直す）。
const INTEGRATIONS: { provider: IntegrationProvider; name: string; description: string }[] = [
  { provider: "freee", name: "freee 会計", description: "あなたが承認者になっている承認待ちの経費申請・各種申請・支払依頼を、タスクとして登録します。" },
  { provider: "moneyforward", name: "マネーフォワード クラウド請求書", description: "入金期日が 7 日以内に来る、または期日を過ぎた未入金の請求書を、入金期日を期限にしたタスクとして登録します。" },
  { provider: "kintone", name: "kintone", description: "指定したアプリに接続後に追加されたレコードを、タスクとして登録します。" },
  { provider: "gmail", name: "Google Workspace（Gmail）", description: "接続後に Gmail でスターを付けたメールを、タスクとして登録します。" },
  { provider: "outlook", name: "Microsoft 365（Outlook）", description: "接続後に Outlook でフラグを付けたメールを、タスクとして登録します。フラグの期限はタスクの期限になります。" },
  { provider: "salesforce", name: "Salesforce", description: "あなたに割り当てられた未完了の ToDo を、ToDo の期日を期限にしたタスクとして登録します。" },
];
// 自社の OAuth クライアントを作る場所（詳しい手順はマニュアル「連携（外部サービス）」）。
const CLIENT_HELP: Partial<Record<IntegrationProvider, string>> = {
  freee: "freee の開発者ページ（https://app.secure.freee.co.jp/developers/applications）で事業所を選び、「アプリ管理」→「新規追加」でアプリタイプ「プライベート」のアプリを作ります。「基本情報」タブのコールバック URL に下の URL を入れて保存し、「権限設定」タブで「[会計] 経費精算」「[会計] 各種申請」「[会計] 支払依頼」「[会計] ユーザー関連情報」の「参照」にチェックを入れます。「基本情報」タブの Client ID と Client Secret を入力してください。",
  moneyforward: "マネーフォワード クラウドのアプリポータル（https://app-portal.moneyforward.com/）でアプリを登録します。リダイレクト URI に下の URL を入れ、クライアント認証方式は CLIENT_SECRET_BASIC を選びます。アプリ詳細画面の Client ID と Client Secret を入力してください。",
  kintone: "cybozu.com 共通管理者が「cybozu.com 共通管理」→「OAuth」→「高度な連携を設定する」→「OAuth クライアントの追加」で、リダイレクトエンドポイントに下の URL を入れて追加します。追加後に「利用者の設定」で接続する人にチェックを入れてください。表示されるクライアント ID とクライアントシークレットを入力してください。",
  salesforce: "Salesforce の設定で「アプリケーションマネージャー」を開き、「新規外部クライアントアプリケーション」を作ります（配布状態は「ローカル」）。OAuth を有効にしてコールバック URL に下の URL を入れ、OAuth 範囲に「API を使用してユーザーデータを管理（api）」「いつでも要求を実行（refresh_token, offline_access）」「一意のユーザー識別子にアクセス（openid）」「ID URL サービスにアクセス（id, profile, email, address, phone）」を選び、Web サーバーフローを有効にします。コンシューマー鍵とコンシューマーの秘密を入力してください。",
};
const CONNECT_ERRORS: Record<string, string> = {
  denied: "接続を取りやめました。",
  expired: "接続の手続きの有効期限（10 分）が切れました。もう一度「接続する」から始めてください。",
  invalid_state: "接続の手続きを確認できませんでした。もう一度「接続する」から始めてください。",
  token_exchange: "連携先から許可を受け取れませんでした。もう一度お試しください。自社の OAuth クライアントを使っている場合は、クライアント ID・クライアントシークレットと、各サービス側に登録したリダイレクト URI を確認してください。",
  account: "連携先のアカウントを読めませんでした。kintone の場合は、アプリ ID と、OAuth クライアントの「利用者の設定」に自分が入っているかを確認してください。",
  config: "現在この連携先には接続できません。自社の OAuth クライアントを使っている場合は、保存されているか確認してください。",
};
const formatTime = (ms: number | null | undefined) => ms ? new Date(ms).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" }) : null;
function connectionState(c: IntegrationConnection): string {
  if (c.status === "failed") return "再接続が必要です";
  if (c.last_error) return "接続済み（直近の同期でエラー）";
  return "接続済み";
}
function loginLabel(url?: string): string {
  if (!url || url === "https://login.salesforce.com") return "";
  if (url === "https://test.salesforce.com") return "（Sandbox）";
  try { return `（${new URL(url).host}）`; } catch { return ""; }
}

function IntegrationsTab(): JSX.Element {
  const session = useSession();
  if (!session) return <></>;
  const scope = sessionScope(session);
  return <ScopedIntegrationsTab key={scope} scope={scope} />;
}

function ScopedIntegrationsTab({ scope }: { scope: string }): JSX.Element {
  const connections = useQuery({ queryKey: ["integrations", scope], queryFn: () => apiGet<IntegrationsResponse>("/v1/integrations") });
  const [openItems, setOpenItems] = useState<string | null>(null);
  const items = useQuery({ queryKey: ["integration-items", scope, openItems], queryFn: () => listSyncedItems(openItems!), enabled: !!openItems });
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<{ provider: IntegrationProvider; kind: "client" | "connect" } | null>(null);
  const [client, setClient] = useState({ client_id: "", client_secret: "", subdomain: "" });
  const [appIds, setAppIds] = useState("");
  const [sfLogin, setSfLogin] = useState<"production" | "sandbox" | "mydomain">("production");
  const [sfDomain, setSfDomain] = useState("");
  useEffect(() => {
    const provider = params.get("integration"), result = params.get("result");
    if (!provider || !result) return;
    const name = INTEGRATIONS.find(i => i.provider === provider)?.name ?? provider;
    if (result === "connected") { setNotice(`${name} に接続しました。`); setError(null); }
    else { setError(`${name}: ${CONNECT_ERRORS[params.get("reason") ?? ""] ?? "接続できませんでした。"}`); setNotice(null); }
    setParams(current => { current.delete("integration"); current.delete("result"); current.delete("reason"); return current; }, { replace: true });
  }, [params, setParams]);
  const run = async (key: string, work: () => Promise<void>) => {
    setBusy(key); setError(null); setNotice(null);
    try { await work(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const connect = (provider: IntegrationProvider, body: Record<string, unknown> = {}) => run(provider, async () => {
    const returnUrl = `${window.location.origin}${window.location.pathname}`;
    const result = await apiPost<{ authorize_url?: string }>(`/v1/integrations/${provider}/connect`, { ...body, return_url: returnUrl });
    if (!result.authorize_url) throw new Error("連携先の認可画面の URL を受け取れませんでした。");
    window.location.assign(result.authorize_url);
  });
  const openForm = (provider: IntegrationProvider, kind: "client" | "connect") => {
    const saved = connections.data?.clients?.[provider];
    setForm({ provider, kind });
    setClient({ client_id: kind === "client" ? saved?.client_id ?? "" : "", client_secret: "", subdomain: kind === "client" ? saved?.subdomain ?? "" : "" });
    setAppIds(""); setSfLogin("production"); setSfDomain("");
  };
  const startConnect = (provider: IntegrationProvider) => {
    if (provider === "kintone" || provider === "salesforce") openForm(provider, "connect"); else void connect(provider);
  };
  const saveClient = (provider: IntegrationProvider) => run(`${provider}:client`, async () => {
    await api(`/v1/integrations/${provider}/client`, { method: "PUT", body: JSON.stringify(client) });
    await connections.refetch();
    setNotice("OAuth クライアントを保存しました。続けて「接続する」を押してください。");
    setClient({ client_id: "", client_secret: "", subdomain: "" });
    setForm(null);
  });
  const deleteClient = (provider: IntegrationProvider, name: string) => {
    if (!window.confirm(`${name} の OAuth クライアントの登録を削除しますか？このクライアントで接続している連携は、同期できなくなります。`)) return;
    void run(`${provider}:client`, async () => { await apiDelete(`/v1/integrations/${provider}/client`); await connections.refetch(); setNotice("OAuth クライアントの登録を削除しました。"); });
  };
  const disconnect = (name: string, c: IntegrationConnection) => {
    if (!window.confirm(`${name}（${c.account_label ?? ""}）との連携を解除しますか？登録済みのタスクは残ります。`)) return;
    void run(c.id, async () => { await apiDelete(`/v1/integrations/${encodeURIComponent(c.id)}`); setOpenItems(null); await connections.refetch(); setNotice(`${name} との連携を解除しました。`); });
  };
  const syncNow = (c: IntegrationConnection) => run(`${c.id}:sync`, async () => {
    const result = await apiPost<{ result: { created: number; error: string | null } }>(`/v1/integrations/${encodeURIComponent(c.id)}/sync`, {});
    await connections.refetch();
    if (openItems === c.id) await items.refetch();
    if (result.result.error) setError(`同期できませんでした: ${result.result.error}`);
    else setNotice(result.result.created > 0 ? `${result.result.created} 件のタスクを登録しました。` : "新しく登録するものはありませんでした。");
  });
  const data = connections.data;
  const canManage = data?.can_manage === true;
  const inputClass = "h-10 w-full rounded-md border border-border-default bg-canvas px-3 text-sm focus-visible:shadow-focus";
  const copyField = (value: string, label: string) => <input readOnly value={value} onFocus={e => e.currentTarget.select()} className={`${inputClass} font-mono text-xs`} aria-label={label} />;
  return <><SectionHeader title="外部サービス連携" description="外部サービスのメール・申請・請求書・レコード・ToDo を、エールチャットのタスクとして取り込みます。" />
    {(error || connections.error) && <p role="alert" className="mb-3 text-sm text-danger">{error || connections.error?.message}</p>}
    {notice && <p role="status" className="mb-3 text-sm text-fg-secondary">{notice}</p>}
    <Card title="サービス" description="取り込みはおよそ 10 分ごとです。登録したタスクは、接続した人が担当者になり、タスク画面に表示されます。接続を継続している間は、同じ項目を二度登録しません。">
      {connections.isPending ? <p role="status">接続状態を確認しています…</p> : <ul className="space-y-3">{INTEGRATIONS.map(item => {
        const list = (data?.integrations ?? []).filter(c => c.provider === item.provider);
        const available = data?.available?.[item.provider] === true;
        const selfClient = (data?.client_setup ?? []).includes(item.provider);
        const saved = data?.clients?.[item.provider];
        const formKind = form?.provider === item.provider ? form.kind : null;
        let action: JSX.Element | null = null;
        if (!available && !(canManage && selfClient)) action = <span className="shrink-0 text-sm text-fg-muted">利用できません</span>;
        else if (!canManage || formKind) action = null;
        else if (!available) action = <Button size="sm" className="shrink-0 whitespace-nowrap" disabled={!!busy} onClick={() => openForm(item.provider, "client")}>設定して接続</Button>;
        else action = <Button size="sm" className="shrink-0 whitespace-nowrap" disabled={!!busy} onClick={() => startConnect(item.provider)}>{busy === item.provider ? "処理中…" : list.length ? "別のアカウントを接続" : "接続する"}</Button>;
        return <li key={item.provider} className="rounded border p-4 break-words">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1 basis-64"><p className="font-medium">{item.name}</p><p className="mt-0.5 text-xs text-fg-muted">{item.description}</p>{list.length === 0 && <p className="mt-1 text-xs text-fg-muted">未接続</p>}</div>
            {action}
          </div>
          {canManage && selfClient && saved && formKind !== "client" && <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
            <span>自社の OAuth クライアント: 登録済み（クライアント ID: {saved.client_id}{saved.subdomain ? `、${saved.subdomain}.cybozu.com` : ""}）</span>
            <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => openForm(item.provider, "client")}>変更</Button>
            <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => deleteClient(item.provider, item.name)}>削除</Button>
          </div>}
          {list.map(c => <div key={c.id} className="mt-3 rounded bg-surface-base p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><p>{c.account_label ?? item.name}{loginLabel(c.login_url)}: {connectionState(c)}</p>
                <p className="text-xs text-fg-muted">{formatTime(c.last_synced_at) ? `最終同期: ${formatTime(c.last_synced_at)}` : "まだ同期していません"}</p>
                {c.apps && c.apps.length > 0 && <p className="text-xs text-fg-muted">取り込むアプリ: {c.apps.map(a => a.name ? `${a.name}（${a.app_id}）` : a.app_id).join("、")}</p>}
                {c.last_error && <p className="text-xs text-danger">エラー: {c.last_error}</p>}</div>
              <div className="flex max-w-full flex-wrap gap-2 [&>button]:shrink-0 [&>button]:whitespace-nowrap">
                {c.can_read_items === true && <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => setOpenItems(c.id)}>取込内容</Button>}
                {canManage && c.status !== "failed" && <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => void syncNow(c)}>{busy === `${c.id}:sync` ? "同期中…" : "今すぐ同期"}</Button>}
                {canManage && <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => disconnect(item.name, c)}>{busy === c.id ? "処理中…" : "切断"}</Button>}
              </div>
            </div>
          </div>)}
          {!canManage && available && <p className="mt-2 text-xs text-fg-muted">接続・切断はワークスペースの所有者・管理者が行えます。</p>}
          {formKind === "client" && <form className="mt-3 space-y-3 text-sm" onSubmit={e => { e.preventDefault(); void saveClient(item.provider); }}>
            <p className="text-xs text-fg-muted">{CLIENT_HELP[item.provider]}</p>
            <label className="flex flex-col gap-1 text-xs">リダイレクト URI（各サービス側に登録する URL）{copyField(data?.redirect_uris?.[item.provider] ?? "", "リダイレクト URI")}</label>
            {(data?.scopes?.[item.provider] ?? []).length > 0 && <p className="text-xs text-fg-muted">必要なスコープ（権限）: <span className="font-mono">{(data?.scopes?.[item.provider] ?? []).join(" ")}</span></p>}
            {item.provider === "kintone" && <label className="flex flex-col gap-1">サブドメイン<span className="flex items-center gap-1"><span>https://</span><input required value={client.subdomain} onChange={e => setClient({ ...client, subdomain: e.target.value })} className={inputClass} placeholder="example" /><span>.cybozu.com</span></span></label>}
            <label className="flex flex-col gap-1">クライアント ID<input required value={client.client_id} onChange={e => setClient({ ...client, client_id: e.target.value })} className={inputClass} autoComplete="off" /></label>
            <label className="flex flex-col gap-1">クライアントシークレット<input required type="password" value={client.client_secret} onChange={e => setClient({ ...client, client_secret: e.target.value })} className={inputClass} autoComplete="new-password" /><span className="text-xs text-fg-muted">保存後は画面に表示しません。変更するときは入力し直してください。</span></label>
            <div className="flex gap-2"><Button size="sm" type="submit" disabled={!!busy}>{busy === `${item.provider}:client` ? "保存中…" : "保存"}</Button><Button size="sm" variant="secondary" type="button" onClick={() => setForm(null)}>やめる</Button></div>
          </form>}
          {formKind === "connect" && item.provider === "kintone" && <form className="mt-3 space-y-3 text-sm" onSubmit={e => { e.preventDefault(); void connect("kintone", { app_ids: appIds, subdomain: client.subdomain }); }}>
            {!saved?.subdomain && <label className="flex flex-col gap-1">サブドメイン<input required value={client.subdomain} onChange={e => setClient({ ...client, subdomain: e.target.value })} className={inputClass} placeholder="example" /></label>}
            <label className="flex flex-col gap-1">取り込むアプリのアプリ ID（複数はカンマ区切り）<input required value={appIds} onChange={e => setAppIds(e.target.value)} className={inputClass} placeholder="12, 34" /><span className="text-xs text-fg-muted">アプリを開いたときの URL「/k/数字/」の数字です。</span></label>
            <div className="flex flex-wrap gap-2"><Button size="sm" type="submit" disabled={!!busy}>{busy === "kintone" ? "処理中…" : "kintone の認可画面へ進む"}</Button><Button size="sm" variant="secondary" type="button" onClick={() => setForm(null)}>やめる</Button></div>
          </form>}
          {formKind === "connect" && item.provider === "salesforce" && <form className="mt-3 space-y-3 text-sm" onSubmit={e => { e.preventDefault(); void connect("salesforce", { login_url: sfLogin === "mydomain" ? sfDomain : sfLogin }); }}>
            <label className="flex flex-col gap-1">ログイン先<select value={sfLogin} onChange={e => setSfLogin(e.target.value as "production" | "sandbox" | "mydomain")} className={inputClass}><option value="production">本番環境（login.salesforce.com）</option><option value="sandbox">Sandbox（test.salesforce.com）</option><option value="mydomain">My Domain（https://〜.my.salesforce.com）</option></select></label>
            {sfLogin === "mydomain" && <label className="flex flex-col gap-1">My Domain の URL<input required value={sfDomain} onChange={e => setSfDomain(e.target.value)} className={inputClass} placeholder="https://example.my.salesforce.com" /></label>}
            <div className="flex flex-wrap gap-2"><Button size="sm" type="submit" disabled={!!busy}>{busy === "salesforce" ? "処理中…" : "Salesforce の認可画面へ進む"}</Button><Button size="sm" variant="secondary" type="button" onClick={() => setForm(null)}>やめる</Button></div>
          </form>}
        </li>;
      })}</ul>}
    </Card>
    {openItems && <Card title="取り込んだ項目">
      {items.isPending && <p role="status">読み込んでいます…</p>}
      {items.error && <p role="alert" className="text-danger">{items.error.message}</p>}
      <ul className="space-y-2">{(items.data?.items ?? []).map(item => <li key={item.external_id} className="text-sm">
        <p>{item.title || item.external_id}</p>
        {item.body_preview && <p className="text-fg-muted">{item.body_preview}</p>}
        {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="underline">サービスで開く</a>}
      </li>)}</ul>
      {items.data?.items.length === 0 && <p className="text-sm text-fg-muted">まだ取り込んだ項目はありません。</p>}
    </Card>}
    </>;
}

/* -------------------------------------------------------------------------- */
/* Billing                                                                    */
/* -------------------------------------------------------------------------- */

function BillingTab(): JSX.Element { return <BillingPanel />; }

/* -------------------------------------------------------------------------- */
/* Security                                                                   */
/* -------------------------------------------------------------------------- */

interface SamlSettings { sp_entity_id?: string; acs_url?: string; metadata_url?: string; configured?: boolean; display_name?: string; entity_id?: string; sso_url?: string; slo_url?: string; idp_certificate?: string; email_domain_allowlist?: string[]; enabled?: boolean; }
interface SecuritySettings { access_token_ttl_seconds: number; mfa_available: boolean; passkey_available: boolean; saml_sign_in_available?: boolean; saml_sign_in_reason?: string; saml?: SamlSettings; scim?: { configured: boolean; base_url?: string }; }
interface ScimTokenInfo { id: string; name: string; token_prefix: string; scope: string; expires_at: number; last_used_at?: number | null; created_at: number; revoked_at?: number | null; }
function SecurityTab(): JSX.Element {
  const security = useQuery({ queryKey: ["security"], refetchOnWindowFocus: false, queryFn: () => apiGet<SecuritySettings>("/v1/admin/security") });
  const tokens = useQuery({ queryKey: ["scim-tokens"], queryFn: () => apiGet<{ tokens: ScimTokenInfo[]; base_url: string }>("/v1/admin/security/scim/tokens") });
  const [saml, setSaml] = useState<SamlSettings>({ display_name: "", entity_id: "", sso_url: "", idp_certificate: "", slo_url: "", enabled: true, email_domain_allowlist: [] });
  const [domains, setDomains] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tokenName, setTokenName] = useState("");
  const [scope, setScope] = useState("users_groups:rw");
  const [days, setDays] = useState(90);
  const [issued, setIssued] = useState("");
  useEffect(() => { if (security.data?.saml) { setSaml(current => ({ ...current, ...security.data!.saml })); setDomains((security.data.saml.email_domain_allowlist || []).join("\n")); } }, [security.data]);
  const perform = async (work: () => Promise<void>) => { setBusy(true); setError(null); try { await work(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); } };
  const saveSaml = () => perform(async () => {
    await api("/v1/admin/security/saml", { method: "PUT", body: JSON.stringify({ display_name: saml.display_name, entity_id: saml.entity_id, sso_url: saml.sso_url, slo_url: saml.slo_url || undefined, idp_certificate: saml.idp_certificate, enabled: saml.enabled, email_domain_allowlist: domains.split(/[\n,]/).map(domain => domain.trim().toLowerCase()).filter(Boolean) }) });
    await security.refetch(); emitToast("success", "SAML 設定を保存しました");
  });
  const issue = () => perform(async () => { setIssued(""); const result = await apiPost<{ token: string }>("/v1/admin/security/scim/tokens", { name: tokenName.trim(), scope, expires_in_days: days }); setIssued(result.token); await tokens.refetch(); await security.refetch(); });
  return <><SectionHeader title="セキュリティ" description="組織の認証設定と自動プロビジョニングを管理します。" /><ToastStack />
    {(error || security.error) && <p role="alert" className="text-danger">{error || security.error?.message}</p>}
    {security.isPending && <p role="status">設定を読み込んでいます…</p>}
    <PersonalSecurity/>
    <Card title="シングルサインオン (SAML)" description="IdP の設定値と署名証明書を登録してください。ワークスペースの所有者・管理者が設定できます。" footer={<Button disabled={busy || !security.data || !saml.entity_id || !saml.sso_url || !saml.idp_certificate || !saml.display_name} onClick={() => void saveSaml()}>SAML 設定を保存</Button>}>
      {security.data && <div role="status" className="mb-4 rounded-md border border-border-default bg-surface-base p-3 text-sm"><p>{security.data.saml_sign_in_available ? 'SSOログインを利用できます。初回はプロフィール設定からアカウントを紐付けてください。' : 'SSOログインには、有効なIdP設定が必要です。'}</p></div>}
      <p className="mb-4">{security.data ? security.data.saml?.configured ? "設定済み" : "未設定" : "状態を確認しています…"}</p>
      <div className="space-y-3 mb-5">{security.data?.saml?.sp_entity_id && <Field label="SP Entity ID"><Input readOnly value={security.data.saml.sp_entity_id} /></Field>}{security.data?.saml?.acs_url && <Field label="ACS URL"><Input readOnly value={security.data.saml.acs_url} /></Field>}{security.data?.saml?.metadata_url && <Field label="SP メタデータ URL"><Input readOnly value={security.data.saml.metadata_url} /></Field>}</div>
      <div className="space-y-3"><Field label="IdP 表示名"><Input value={saml.display_name || ""} onChange={e => setSaml({ ...saml, display_name: e.target.value })} /></Field>
        <Field label="IdP Entity ID"><Input value={saml.entity_id || ""} onChange={e => setSaml({ ...saml, entity_id: e.target.value })} /></Field>
        <Field label="SSO URL"><Input type="url" value={saml.sso_url || ""} onChange={e => setSaml({ ...saml, sso_url: e.target.value })} /></Field>
        <Field label="SLO URL（任意）"><Input type="url" value={saml.slo_url || ""} onChange={e => setSaml({ ...saml, slo_url: e.target.value })} /></Field>
        <Field label="IdP 署名証明書（PEM）" helperText="公開鍵証明書を貼り付けてください。秘密鍵は入力しないでください。"><Textarea rows={6} value={saml.idp_certificate || ""} onChange={e => setSaml({ ...saml, idp_certificate: e.target.value })} className="font-mono text-xs" /></Field>
        <Field label="許可するメールドメイン（1行1件、任意）"><Textarea rows={2} value={domains} onChange={e => setDomains(e.target.value)} /></Field>
        <ToggleRow label="SAML ログインを有効にする" checked={saml.enabled ?? true} onChange={enabled => setSaml({ ...saml, enabled })} />
      </div>
    </Card>
    <Card title="SCIM 2.0 自動プロビジョニング" description="IdP にエンドポイントと発行したトークンを設定してください。">
      {tokens.error && <p role="alert" className="text-danger">{tokens.error.message}</p>}
      {(tokens.data?.base_url || security.data?.scim?.base_url) && <Field label="SCIM エンドポイント"><Input value={tokens.data?.base_url || security.data?.scim?.base_url || ""} readOnly /></Field>}
      <div className="space-y-3 mt-4"><Field label="トークン名"><Input value={tokenName} maxLength={100} onChange={e => setTokenName(e.target.value)} /></Field>
        <Field label="権限"><select value={scope} onChange={e => setScope(e.target.value)} className="border rounded p-2"><option value="users_groups:rw">ユーザーとグループの読み書き</option><option value="users:rw">ユーザーの読み書き</option><option value="users:r">ユーザーの読み取り</option></select></Field>
        <Field label="有効日数"><Input type="number" min={1} max={365} value={days} onChange={e => setDays(Number(e.target.value))} /></Field>
        <Button disabled={busy || !tokenName.trim() || !security.data || days < 1 || days > 365} onClick={() => void issue()}>SCIM トークンを発行</Button>
      </div>
      {issued && <div role="status" className="mt-4 space-y-2"><p>トークンはこの画面で一度だけ確認できます。安全に保存してください。</p><Input aria-label="発行した SCIM トークン" value={issued} readOnly className="font-mono text-xs" /><Button variant="secondary" size="sm" onClick={() => void perform(async () => { await navigator.clipboard.writeText(issued); emitToast("success", "トークンをコピーしました"); })}>コピー</Button><Button variant="ghost" size="sm" onClick={() => setIssued("")}>表示を閉じる</Button></div>}
      <ul className="mt-4 space-y-2">{tokens.data?.tokens.map(token => <li key={token.id} className="border rounded p-3 flex flex-wrap gap-3 items-center"><div className="flex-1"><p>{token.name} <span className="font-mono text-xs">{token.token_prefix}…</span></p><p className="text-xs text-fg-muted">{token.scope} · {token.revoked_at ? "失効済み" : `期限: ${new Date(token.expires_at).toLocaleDateString()}`}</p></div>{!token.revoked_at && <Button size="sm" variant="secondary" disabled={busy} onClick={() => { if (window.confirm(`${token.name} を失効させますか？ このトークンを使う同期が停止します。`)) void perform(async () => { await apiDelete(`/v1/admin/security/scim/tokens/${encodeURIComponent(token.id)}`); await tokens.refetch(); }); }}>失効</Button>}</li>)}</ul>
    </Card>
    {security.data && <Card title="セッション"><p>アクセストークン有効期限: {Math.round(security.data.access_token_ttl_seconds / 60)} 分</p></Card>}
  </>;
}

/* -------------------------------------------------------------------------- */
/* DLP                                                                        */
/* -------------------------------------------------------------------------- */

interface LiveDlpConfig { enabled: boolean; rules: { rule_id: string; enabled: boolean; action: "block" | "mask" | "warn" | "allow" }[]; mask_replacement: string; custom_keywords: string[]; }
function DlpTab(): JSX.Element {
  const query = useQuery({ queryKey: ["dlp"], refetchOnWindowFocus: false, queryFn: () => apiGet<{ config: LiveDlpConfig }>("/v1/admin/dlp/config") });
  const [config, setConfig] = useState<LiveDlpConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (query.data) setConfig(query.data.config); }, [query.data]);
  const save = async () => { if (!config) return; setSaving(true); setError(null);
    try { await api("/v1/admin/dlp/config", { method: "PUT", body: JSON.stringify(config) }); await query.refetch(); emitToast("success", "DLP 設定を保存しました"); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setSaving(false); }
  };
  const labels: Record<string, string> = { my_number: "マイナンバー", credit_card: "クレジットカード", phone_jp: "電話番号", corporate_number: "法人番号", postal_address: "住所", bank_account: "銀行口座", secret_key: "秘密鍵・パスワード", email_external: "メールアドレス" };
  return <><SectionHeader title="DLP / マスキング" description="送信内容の検出ルールとアクションを管理します。" /><ToastStack />
    {(error || query.error) && <p role="alert" className="text-danger">{error || query.error?.message}</p>}
    {config && <Card title="検出ルール"><ToggleRow label="DLP を有効にする" description="保存後のメッセージ送信に適用します。" checked={config.enabled} onChange={enabled => setConfig({ ...config, enabled })} />
      <ul className="space-y-3 my-5">{config.rules.map(rule => <li key={rule.rule_id} className="flex flex-wrap items-center justify-between gap-3 border rounded p-3"><span>{labels[rule.rule_id] || rule.rule_id}</span>
        <Toggle checked={rule.enabled} label={`${labels[rule.rule_id] || rule.rule_id} を有効化`} onChange={enabled => setConfig({ ...config, rules: config.rules.map(r => r.rule_id === rule.rule_id ? { ...r, enabled } : r) })} />
        <select aria-label={`${rule.rule_id} のアクション`} value={rule.action} onChange={e => setConfig({ ...config, rules: config.rules.map(r => r.rule_id === rule.rule_id ? { ...r, action: e.target.value as typeof rule.action } : r) })}><option value="warn">警告</option><option value="mask">マスク</option><option value="block">送信ブロック</option><option value="allow">許可</option></select>
      </li>)}</ul>
      <Field label="カスタム禁止キーワード（1行1件）"><Textarea value={config.custom_keywords.join("\n")} onChange={e => setConfig({ ...config, custom_keywords: e.target.value.split("\n") })} /></Field>
      <Field label="マスク置換文字列"><Input value={config.mask_replacement} onChange={e => setConfig({ ...config, mask_replacement: e.target.value })} /></Field>
      <Button onClick={() => void save()} disabled={saving}>保存</Button></Card>}
    {query.isPending && <p role="status">設定を読み込んでいます…</p>}
  </>;
}

/* -------------------------------------------------------------------------- */
/* Audit                                                                      */
/* -------------------------------------------------------------------------- */

interface AuditEvent {
  ts: string;
  actor: string;
  action: string;
  target: string;
  ip: string;
}

const AUDIT_LOG: AuditEvent[] = [
  {
    ts: "2026-05-09 09:42:13",
    actor: "alice@example.com",
    action: "auth.login",
    target: "session/01J9...",
    ip: "203.0.113.18",
  },
  {
    ts: "2026-05-09 09:38:01",
    actor: "bob@example.com",
    action: "task.create",
    target: "tsk_01J9...",
    ip: "203.0.113.42",
  },
  {
    ts: "2026-05-09 09:30:55",
    actor: "you@example.com",
    action: "settings.dlp.update",
    target: "rule:mynumber",
    ip: "192.0.2.7",
  },
  {
    ts: "2026-05-08 18:11:22",
    actor: "carol@example.com",
    action: "file.share_link.create",
    target: "file_01J9...",
    ip: "192.0.2.10",
  },
];

function AuditTab(): JSX.Element {
  const [query, setQuery] = useState("");
  const logQuery = useQuery({ queryKey: ["audit"], queryFn: () => apiGet<{ rows: { occurred_at: number; actor_email: string | null; actor_id: string | null; event_type: string; target_label: string | null; target_id: string | null; actor_ip: string | null }[] }>("/v1/audit/logs?limit=100") });
  const exportCsv = async () => {
    try { const response = await authenticatedFetch(resolveApiUrl("/v1/audit/export.csv")); if (!response.ok) throw new Error(`エクスポートに失敗しました (${response.status})`); const url = URL.createObjectURL(await response.blob()); const link = document.createElement("a"); link.href = url; link.download = "audit.csv"; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
    catch (e) { emitToast("danger", e instanceof Error ? e.message : String(e)); }
  };
  const events = (logQuery.data?.rows || []).map(row => ({ ts: new Date(row.occurred_at).toLocaleString(), actor: row.actor_email || row.actor_id || "system", action: row.event_type, target: row.target_label || row.target_id || "", ip: row.actor_ip || "" })).filter((e) =>
    (e.actor + e.action + e.target).toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <>
      <SectionHeader
        title="監査ログ"
        description="管理操作とアクセスの記録を確認・エクスポートします。"
      />
      <ToastStack />

      {logQuery.error && <p role="alert" className="text-danger">{logQuery.error.message}</p>}
      {logQuery.isPending && <p role="status">監査ログを読み込んでいます…</p>}
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input
            type="search"
            placeholder="アクター・アクションで検索..."
            aria-label="監査ログを検索"
            className="!h-9 max-w-xs"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto"
            onClick={() => void exportCsv()}
          >
            CSV エクスポート
          </Button>
        </div>

        <div className="overflow-hidden rounded-md ring-1 ring-border-subtle">
          <table className="w-full text-xs">
            <thead className="bg-surface-base text-left text-fg-muted">
              <tr>
                <th className="px-3 py-2 font-medium">日時</th>
                <th className="px-3 py-2 font-medium">アクター</th>
                <th className="px-3 py-2 font-medium">アクション</th>
                <th className="px-3 py-2 font-medium">対象</th>
                <th className="px-3 py-2 font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle bg-canvas font-mono">
              {events.map((e, i) => (
                <tr key={i} className="hover:bg-surface-base">
                  <td className="px-3 py-2 text-fg-secondary">{e.ts}</td>
                  <td className="px-3 py-2">{e.actor}</td>
                  <td className="px-3 py-2 text-brand-700">{e.action}</td>
                  <td className="px-3 py-2 text-fg-secondary">{e.target}</td>
                  <td className="px-3 py-2 text-fg-muted">{e.ip}</td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-sm text-fg-muted"
                  >
                    一致するイベントがありません。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Compliance                                                                 */
/* -------------------------------------------------------------------------- */

function ComplianceTab(): JSX.Element {
  const region = useQuery({ queryKey: ["region"], queryFn: () => apiGet<{ primary: string; replica: string; updated_at: number; note?: string; residency_verified: boolean }>("/v1/admin/region") });
  const [error, setError] = useState<string | null>(null);
  const downloadCertificate = async () => {
    try { const response = await authenticatedFetch(resolveApiUrl("/v1/admin/region/residency-cert")); if (!response.ok) throw new Error(`保証書を取得できませんでした (${response.status})`); const url = URL.createObjectURL(await response.blob()); const link = document.createElement("a"); link.href = url; link.download = "data-residency.pdf"; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  return <><SectionHeader title="コンプライアンス" description="データの保管リージョンと保証書を確認します。" />
    {(region.error || error) && <p role="alert" className="text-danger">{error || region.error?.message}</p>}
    <Card title="データレジデンシー">{region.isPending ? <p role="status">読み込んでいます…</p> : region.data && <dl className="space-y-3"><dt>Primary</dt><dd>{region.data.primary}</dd><dt>Replica</dt><dd>{region.data.replica}</dd><dt>更新日時</dt><dd>{region.data.updated_at ? new Date(region.data.updated_at).toLocaleString() : "未登録"}</dd></dl>}<p className="mt-4 text-sm text-fg-secondary">{region.data?.note || (region.data && !region.data.residency_verified ? "保管場所の保証は確認されていません。保証書は確認完了後に取得できます。" : "")}</p><Button className="mt-4" disabled={!region.data?.residency_verified} onClick={() => void downloadCertificate()}>データレジデンシー保証書を取得</Button></Card>
    <RetentionSettings/>
  </>;
}
